import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {
    formatName,
    getIdentityValidationTypeById,
    IDENTITY_DOCUMENTS_MODEL,
    IDENTITY_VALIDATION_CARD_MODEL, IDENTITY_VALIDATION_TYPE_MODEL
} from "../../Visitor/Model/Model";
import {VISITOR_MODEL} from "../../Model/Model";
import {GetAllUserFiles} from "../../../../../reduxToolkit/UserFile/UserFileApi";
import {GetInvitationById} from "../../../../../reduxToolkit/Invitation/InvitationApi";
import {toast} from "react-toastify";
import {DownloadDataZip, ZipDownloadByUserId} from "../../../../../reduxToolkit/fileUpload/FileUploadApi";
import {errorMsg, getFullName} from "../../../../../Apis/functions";
import {useTranslation} from "react-i18next";
import {GetUserRestrictionVisitor} from "../../../../../reduxToolkit/Visitor/VisitorApi";
import {enableBiometricByUserTypeId, getUpdatePermissionByUserTypeId} from "../Model/Model";
import {GetUserDetailByUserId} from "../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";

/**
 * Custom hook responsible for handling the identity verification flow data,
 * including user information, uploaded identity documents, validation status,
 * biometric configuration, and file download actions.
 *
 * <p>This hook:
 * <ul>
 *   <li>Retrieves user restriction settings.</li>
 *   <li>Determines whether biometric validation is enabled based on the user type.</li>
 *   <li>Loads user detail information and identity validation status.</li>
 *   <li>Retrieves uploaded identity documents when validation data exists.</li>
 *   <li>Handles invitation data retrieval.</li>
 *   <li>Provides functionality to download uploaded files as a ZIP.</li>
 * </ul>
 * </p>
 *
 * @author Karen Yunuen Pozos Arias
 * @email kpozos@ibl.mx
 * @jobTitle Full Stack Developer
 *
 * @param {Object} params the hook parameters
 * @param {string} params.userId the unique identifier of the user
 * @param {string} params.invitationId the unique identifier of the invitation
 * @param {number} params.userTypeId the identifier of the user type
 *
 * @returns {Object} hook state and handlers
 * @returns {Object} returns.item the current user information
 * @returns {Function} returns.setItem updates the user information state
 * @returns {boolean} returns.disabled indicates whether the view is disabled
 * @returns {Array|null} returns.identityDocuments the processed identity documents
 * @returns {Object|null} returns.identityValidationType the current identity validation type
 * @returns {Object|null} returns.identityValidationStatus the current identity validation status
 * @returns {Function} returns.setModified updates the modified state
 * @returns {boolean} returns.modified indicates whether data should be refreshed
 * @returns {Function} returns.handleDownload downloads user files as a ZIP
 * @returns {boolean} returns.hasIdentityFiles indicates whether identity files exist
 * @returns {boolean} returns.canValidate indicates whether the user holds the update permission of the origin
 */
export const CommonIdentityVerificationApp = ({ userId, invitationId, userTypeId, disabledState }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [item, setItem] = useState({...VISITOR_MODEL});
    const [modified, setModified] = useState(false);
    const [disabled, setDisabled] = useState(disabledState);
    const [identityDocuments, setIdentityDocuments] = useState(null);
    const [assets, setAssets] = useState(null);
    const [identityValidationType, setIdentityValidationType] = useState(null);
    const [identityValidationStatus, setIdentityValidationStatus] = useState(null);
    const hasIdentityFiles = Object.values(assets || {}).some((item) => item?.file);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // The update permission depends on the origin the validation was opened from
    const canValidate = DEV || permission?.includes(getUpdatePermissionByUserTypeId(userTypeId));

    /**
     * Reloads all identity verification related information when the modified
     * state changes to true.
     */
    useEffect(() => {
        if(modified){
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Loads the initial identity verification data when the component is mounted.
     */
    useEffect(() => {
        getData();
    }, []);

    /**
     * Retrieves user restriction settings, user detail information,
     * validation status, identity validation type, uploaded files,
     * and biometric configuration according to the user type.
     */
    const getData = () => {
        dispatch(GetUserRestrictionVisitor()).then(({payload: {data: { success, data }}}) => {
            if(success){
                const enableBiometric = enableBiometricByUserTypeId({ userTypeId, userRestrictions: data });

                dispatch(GetUserDetailByUserId(userId)).then(({payload: {data: { data }}}) => {
                    const aux = data;
                    if (aux?.id) {
                        setItem(VISITOR_MODEL.set(aux));
                        setIdentityValidationStatus(IDENTITY_VALIDATION_CARD_MODEL.getIdentityValidationStatus(aux?.identityValidation?.id))

                        if(aux?.identityValidation?.id !== IDENTITY_VALIDATION_CARD_MODEL.PENDING_TO_UPLOAD_INFORMATION.id){
                            setIdentityValidationType(getIdentityValidationTypeById(aux?.identityValidationType));
                            dispatch(GetAllUserFiles(userId)).then(({payload: {data: {data}}}) => {
                                setIdentityDocuments(IDENTITY_DOCUMENTS_MODEL.set(data, aux, enableBiometric));
                                setAssets(data);
                            });
                            getInvitationData();
                        }else{
                            setIdentityValidationType(getIdentityValidationTypeById(IDENTITY_VALIDATION_TYPE_MODEL.NONE.id));
                            setIdentityDocuments(IDENTITY_DOCUMENTS_MODEL.set([], aux, enableBiometric));
                        }

                    }
                });
            }
        });
    }

    /**
     * Retrieves invitation information associated with the current user.
     */
    const getInvitationData = () => {
        if(invitationId)
            dispatch(GetInvitationById(invitationId));
    }

    /**
     * Downloads all uploaded identity files associated with the current user
     * as a ZIP file.
     *
     * <p>If no files are available, a warning notification is displayed.</p>
     */
    const handleDownload = () => {
        const assetsId = assets.map(asset => asset?.id);

        if (assetsId?.length <= 0) {
            toast.warn(t('no_files_registered'));
            return;
        }
        const nameZip = formatName(getFullName(item?.name, item?.lastName));
        dispatch(ZipDownloadByUserId(userId)).then(({ payload: { data, code } }) => {
            if (data) {
                DownloadDataZip(data, nameZip)
            } else {
                errorMsg(code)
            }
        })
    }

    return {
        item,
        setItem,
        disabled,
        identityDocuments,
        identityValidationType,
        identityValidationStatus,
        setModified,
        modified,
        handleDownload,
        hasIdentityFiles,
        canValidate
    };
}
